/**
 * Every reducer module's default export is the reducer function itself. In
 * addition, each module should export a type or interface that describes
 * the state of the reducer plus any selector functions. The `* as`
 * notation packages up all of the exports into a single object.
 */
import * as fromColorizer from '@app/colorizer/colorizer.reducer';
import { CheckUserAccountTicketLimitLoginOptions, LoginOptions } from '@app/login/login.model';
import * as fromPressNews from '@app/press-news/press-news.reducer';
import { AccessibilityStoreState } from '@core/data-access/models/accessibility/accessibility-store.model';
import { SharedStoreState } from '@core/data-access/models/shared/shared-store.model';
import { TicketSendingOption } from '@core/data-access/models/shared/ticket-sending-options.model';
import {
  accessibilityReducer,
  accessibilityReducerKey
} from '@core/data-access/store/accessibility/accessibility.reducer';
import { sharedReducer, sharedReducerKey } from '@core/data-access/store/shared/shared.reducer';
import { BuyerInformationStoreState } from '@core/features/buyer-information/models/buyer-information-store.model';
import {
  buyerInformationReducer,
  buyerInformationReducerKey
} from '@core/features/buyer-information/store/buyer-information.reducer';
import { routerReducer, RouterReducerState } from '@ngrx/router-store';
import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import { deactivateCurrentSession } from '@shared/app-utils';
import * as fromAdditionalServices from '@store/additional-services/additional-services.reducer';
import { PromoCodeVisibility } from '@store/customization/customization.interfaces';
import * as fromCustomization from '@store/customization/customization.reducer';
import * as fromExhibition from '@store/exhibition/exhibition.reducer';
import * as fromHelper from '@store/helpers/helper.reducer';
import * as fromLegitimation from '@store/legitimation/legitimation.reducer';
import { getSelectedSendingOption } from '@store/products/product-selection/product-selection.selectors';
import { reducer as productsReducer, State as productsState } from '@store/products/product.reducer';
import { OrderedStepValidities, StepName, StepValidity } from '@store/step-forms/step.interface';
import * as fromStepsForms from '@store/step-forms/steps-forms.reducer';
import { mapStepNameToStepRoute } from '@store/step-forms/utils/step-validity-mappers';
import { reducer as translationReducer, State as translationState } from '@store/translation/translation.reducer';
import * as fromUser from '@store/user/user.reducer';
import { defaultTo, find, includes, isNull } from 'lodash';

/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export interface State {
  router: RouterReducerState;
  [accessibilityReducerKey]: AccessibilityStoreState;
  additionalServices: fromAdditionalServices.State;
  exhibitions: fromExhibition.State;
  user: fromUser.State;
  customization: fromCustomization.State;
  translation: translationState;
  legitimation: fromLegitimation.State;
  stepsForms: fromStepsForms.State;
  [buyerInformationReducerKey]: BuyerInformationStoreState;
  pressnews: fromPressNews.State;
  helper: fromHelper.State;
  [sharedReducerKey]: SharedStoreState;
  colorizer: fromColorizer.State;
  products: productsState;
}

window.onbeforeunload = () => {
  deactivateCurrentSession();
};

window.onunload = () => {
  //we need to do this in onuload event since Safari browser on iOS doesn't support onbeforeunload event:
  deactivateCurrentSession();
};

window.onpagehide = () => {
  //we need to do this in onpagehide event since Safari browser on iOS doesn't support onbeforeunload event:
  deactivateCurrentSession();
};

export const reducers: ActionReducerMap<State> = {
  router: routerReducer,
  [accessibilityReducerKey]: accessibilityReducer,
  additionalServices: fromAdditionalServices.reducer,
  exhibitions: fromExhibition.reducer,
  user: fromUser.reducer,
  customization: fromCustomization.reducer,
  translation: translationReducer,
  legitimation: fromLegitimation.reducer,
  stepsForms: fromStepsForms.reducer,
  [buyerInformationReducerKey]: buyerInformationReducer,
  pressnews: fromPressNews.reducer,
  helper: fromHelper.reducer,
  [sharedReducerKey]: sharedReducer,
  colorizer: fromColorizer.reducer,
  products: productsReducer
};

/*   export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
    return localStorageSync({keys: ['todos']})(reducer);
  } */

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
                                SELECTORS
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export const getState = (state: State) => state;

//#region exhibition
export const getExhibitionState = createFeatureSelector<fromExhibition.State>('exhibitions');

export const getAllExhibitionsCollection = createSelector(getExhibitionState, fromExhibition.getAllExhibitions);
export const getSelectedExhibition = createSelector(getExhibitionState, fromExhibition.getSelectedExhibition);
export const getSelectedExhibitionId = createSelector(getExhibitionState, fromExhibition.getSelectedExhibitionId);
export const getExhibitionHistoryList = createSelector(getExhibitionState, fromExhibition.getExhibitionHistoryList);
export const selectScripts = createSelector(getExhibitionState, fromExhibition.selectScripts);
export const selectAreScriptsLoaded = createSelector(getExhibitionState, fromExhibition.selectAreScriptsLoaded);
export const selectIsFirstLoadDone = createSelector(getExhibitionState, fromExhibition.selectIsFirstLoadDone);
export const getTitles = createSelector(getExhibitionState, fromExhibition.getTitles);
export const getProfessions = createSelector(getExhibitionState, fromExhibition.getProfessions);
export const getDepartments = createSelector(getExhibitionState, fromExhibition.getDepartments);
export const getOccupationalGroups = createSelector(getExhibitionState, fromExhibition.getOccupationalGroups);
export const getAllTitles = createSelector(getExhibitionState, fromExhibition.getAllTitles);
export const getAllProfessions = createSelector(getExhibitionState, fromExhibition.getAllProfessions);
export const getAllDepartments = createSelector(getExhibitionState, fromExhibition.getAllDepartments);
export const getAllOccupationalGroups = createSelector(getExhibitionState, fromExhibition.getAllOccupationalGroups);
//#endregion

//#region helpers
export const getHelpersState = createFeatureSelector<fromHelper.State>('helper');
export const getAllCountriesList = createSelector(getHelpersState, fromHelper.getAllCountries);
export const getLanguage = createSelector(getHelpersState, fromHelper.getLanguage);
export const getSupportedLanguages = createSelector(getHelpersState, fromHelper.getSupportedLanguages);
export const getSelfRegistration = createSelector(getHelpersState, fromHelper.getSelfRegistration);
export const isEventSeriesPage = createSelector(getHelpersState, fromHelper.isEventSeriesPage);
export const getSpinnerValue = createSelector(getHelpersState, fromHelper.getSpinnerValue);
export const getAllCitiesByZipCode = createSelector(getHelpersState, fromHelper.getAllCitiesByZipCode);

export const getReloadRequired = createSelector(getHelpersState, fromHelper.getReloadRequired);
export const getIsWidget = createSelector(getHelpersState, fromHelper.getIsWidget);
export const selectIsLogoutProcessing = createSelector(getHelpersState, fromHelper.selectIsLogoutProcessing);
export const selectInitializeConteoLogin = createSelector(getHelpersState, fromHelper.selectInitializeConteoLogin);
export const selectReinitializeConteoLogin = createSelector(getHelpersState, fromHelper.selectReinitializeConteoLogin);
export const selectLatestInitializedConteoLoginExhibitionId = createSelector(
  getHelpersState,
  fromHelper.selectLatestInitializedConteoLoginExhibitionId
);

//#endregion

//#region users
export const getUserState = createFeatureSelector<fromUser.State>('user');

export const getUser = createSelector(getUserState, fromUser.getUser);
export const getLoginTimestamp = createSelector(getUserState, fromUser.getLoginTimestamp);
export const isProfileLoading = createSelector(getUserState, fromUser.isProfileLoading);
export const getProfile = createSelector(getUserState, fromUser.getProfile);
export const selectUserId = createSelector(getUserState, ({ profile }) => defaultTo(profile?.id, null));
export const getUserLanguage = createSelector(getUserState, fromUser.getLanguage);

export const isUserLoggedIn = createSelector(getUserState, fromUser.isUserLoggedIn);
export const isLoggedInAsAdmin = createSelector(getUserState, fromUser.isLoggedInAsAdmin);
export const getRegistrationQuestionnaire = createSelector(getUserState, fromUser.getRegistrationQuestionnaire);
export const getEventsEmailUnsubscriptions = createSelector(getUserState, fromUser.getEventsEmailUnsubscriptions);
export const getProfileBillingAddress = createSelector(getUserState, fromUser.getProfileBillingAddress);
export const getSelectedBillingAddressId = createSelector(getUserState, fromUser.getSelectedBillingAddressId);
export const selectProfileBillingAddressById = (profileBillingAddressId: number) =>
  createSelector(getProfileBillingAddress, (profileBillingAddresses) =>
    find(profileBillingAddresses, ({ id }) => id === profileBillingAddressId)
  );
export const getPassRecoverySuccess = createSelector(getUserState, fromUser.getPassRecoverySuccess);
export const passChangeStatus = createSelector(getUserState, fromUser.passChangeStatus);

export const emailChangeStatus = createSelector(getUserState, fromUser.emailChangeStatus);

export const emailVerifyStatus = createSelector(getUserState, fromUser.emailVerifyStatus);

export const selectLoginType = createSelector(getUserState, fromUser.selectLoginType);
//#endregion

//#region customization
export const getCustomizationState = createFeatureSelector<fromCustomization.State>('customization');
export const getOperatorsSettings = createSelector(getCustomizationState, fromCustomization.getOperatorsSettings);
export const getWidgetSettings = createSelector(getCustomizationState, fromCustomization.getWidgetSettings);
export const getExhibitionStyles = createSelector(getCustomizationState, fromCustomization.getExhibitionStyles);
export const getSponsorBanner = createSelector(getCustomizationState, fromCustomization.getSponsorBanner);
export const getExhibitionSettings = createSelector(getCustomizationState, fromCustomization.getExhibitionSettings);
export const selectIsEventMaintenanceEnabled = createSelector(
  getCustomizationState,
  fromCustomization.selectIsEventMaintenanceEnabled
);
export const getOperatorsScripts = createSelector(getCustomizationState, fromCustomization.getOperatorsScripts);
export const getLocalTime = createSelector(getExhibitionSettings, (settings) => {
  if (settings) {
    return new Date(settings.localTime);
  }
  return null;
});
export const selectCurrencySettings = createSelector(
  getExhibitionSettings,
  (exhibitionSettings) => exhibitionSettings?.currencySettings
);
export const selectExhibitionSettingsLoginMode = createSelector(
  getExhibitionSettings,
  (exhibitionSettings) => exhibitionSettings?.loginMode
);
export const selectIsOperatorsMaintenanceEnabled = createSelector(
  getCustomizationState,
  fromCustomization.selectIsOperatorsMaintenanceEnabled
);
export const selectExhibitionSettingsIsReadOnlyBuyerAndAccountInfo = createSelector(
  getCustomizationState,
  fromCustomization.selectExhibitionSettingsIsReadOnlyBuyerAndAccountInfo
);
export const selectOperatorSettingsIsReadOnlyBuyerAndAccountInfo = createSelector(
  getCustomizationState,
  fromCustomization.selectOperatorSettingsIsReadOnlyBuyerAndAccountInfo
);
export const selectIsGoogleMapsApiEnabled = createSelector(
  getCustomizationState,
  fromCustomization.selectIsGoogleMapsApiEnabled
);
export const getLoginMode = createSelector(getCustomizationState, fromCustomization.getLoginMode);
export const selectIsSIAEEnabled = createSelector(getCustomizationState, fromCustomization.selectIsSIAEEnabled);
export const selectGoogleReCaptchaSiteKey = createSelector(
  getCustomizationState,
  fromCustomization.selectGoogleReCaptchaSiteKey
);
export const selectIsNF525Enabled = createSelector(getCustomizationState, fromCustomization.selectIsNF525Enabled);
export const getLocalizedImages = createSelector(getCustomizationState, fromCustomization.getLocalizedImages);
export const getOrderUuid = createSelector(getCustomizationState, fromCustomization.getOrderUuid);
export const getShoppingStartTime = createSelector(getCustomizationState, fromCustomization.getShoppingStartTime);
export const ageRating = createSelector(getExhibitionSettings, fromCustomization.ageRating);
export const selectTicketLimitPerEmail = createSelector(
  getExhibitionSettings,
  fromCustomization.selectTicketLimitPerEmail
);
export const selectIsTicketLimitPerEmailActive = createSelector(
  selectTicketLimitPerEmail,
  (ticketLimitPerEmail) => !!ticketLimitPerEmail
);
export const selectIsVoucherIncludedPerUserAccountLimit = createSelector(
  getExhibitionSettings,
  ({ isVoucherIncludedPerUserAccountLimit }) => isVoucherIncludedPerUserAccountLimit
);
export const uniqueVisitorCheckType = createSelector(getExhibitionSettings, fromCustomization.uniqueVisitorCheckType);
export const isDownloadTicketButtonVisible = createSelector(
  getExhibitionSettings,
  fromCustomization.isDownloadTicketButtonVisible
);
export const getTicketLimitPerUserAccount = createSelector(
  getExhibitionSettings,
  fromCustomization.getTicketLimitPerUserAccount
);
export const getTariffReleaseInMinutes = createSelector(
  getCustomizationState,
  fromCustomization.getTariffReleaseInMinutes
);
export const selectInitiateConteoLogin = createSelector(
  getExhibitionSettings,
  fromCustomization.selectInitiateConteoLogin
);
export const selectGoToLegitimationForNewAccount = createSelector(
  getExhibitionSettings,
  (exhibitionSettings) => exhibitionSettings?.goToLegitimationForNewAccount
);
//#endregion

//#region legitimation
export const getLegitimationState = createFeatureSelector<fromLegitimation.State>('legitimation');
export const getLegitimationList = createSelector(getLegitimationState, fromLegitimation.getLegitimationList);
export const getLegitimationStatus = createSelector(getLegitimationState, fromLegitimation.getLegitimationStatus);
export const getLegitimationFaxId = createSelector(getLegitimationState, fromLegitimation.getLegitimationFaxId);
export const getLegitimationPostResponse = createSelector(
  getLegitimationState,
  fromLegitimation.getLegitimationPostResponse
);
//#endregion

//#region steps forms
export const getStepsForms = createFeatureSelector<fromStepsForms.State>('stepsForms');
export const getSteps = createSelector(getStepsForms, fromStepsForms.getSteps);
export const getSelectedStep = createSelector(getStepsForms, fromStepsForms.getSelectedStep);
export const getStepsValidity = createSelector(getStepsForms, fromStepsForms.getStepsValidity);
export const selectSelectedStepValidity = createSelector(
  getSelectedStep,
  getStepsValidity,
  (selectedStep, stepsValidity) => stepsValidity[selectedStep] || null
);
export const selectStepValidityByStepName = (stepName: StepName) =>
  createSelector(getStepsValidity, (stepValidity): StepValidity => stepValidity[stepName] || null);
export const getConfirmationCheckboxesInputs = createSelector(
  getStepsForms,
  fromStepsForms.getConfirmationCheckboxesInputs
);

export const isContinueAsGuest = createSelector(getStepsForms, fromStepsForms.isContinueAsGuest);

export const selectOrderedStepValidities = createSelector(getStepsValidity, (stepsValidity) => {
  const orderedStepValidities: OrderedStepValidities = [];

  for (const stepName in stepsValidity) {
    orderedStepValidities.push({
      key: mapStepNameToStepRoute(stepName as StepName),
      name: stepName as StepName,
      value: stepsValidity[stepName]
    });
  }

  return orderedStepValidities.sort((a, b) => a.value.order - b.value.order);
});

export const getOrderResponse = createSelector(getStepsForms, fromStepsForms.getOrderResponse);

export const getPaymentMethod = createSelector(getStepsForms, fromStepsForms.getPaymentMethod);

export const getAnonymousHiddenSteps = createSelector(getStepsForms, fromStepsForms.getAnonymousHiddenSteps);
//#endregion

//#region press news
export const getPressNewsState = createFeatureSelector<fromPressNews.State>('pressnews');
export const getPressNewsCollection = createSelector(getPressNewsState, fromPressNews.getAll);
export const getSelectedPressNewsId = createSelector(getPressNewsState, fromPressNews.getSelectedPressNewsId);
export const getPressNewsDetail = createSelector(getPressNewsState, fromPressNews.getPressNewsDetail);
//#endregion

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
                            COLORIZER
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
export const getColorizerState = createFeatureSelector<fromColorizer.State>('colorizer');

export const getAllStyles = createSelector(getColorizerState, fromColorizer.allStyleTemplates);

export const getSelectedStyle = createSelector(getColorizerState, fromColorizer.getStyle);

export const getStyleId = createSelector(getColorizerState, fromColorizer.getStyleId);

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
                            COMBINED SELECTORS
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export const showVoucherPromoCodeSection = createSelector(
  isUserLoggedIn,
  getExhibitionSettings,
  (isLoggedIn, settings) => {
    if (settings) {
      return (
        settings.promoCodeVisible === PromoCodeVisibility.AlwaysVisible ||
        (settings.promoCodeVisible === PromoCodeVisibility.AfterLogin && isLoggedIn)
      );
    }
    return true;
  }
);

export const selectIsTicketAssignmentStepVisible = createSelector(
  getSelfRegistration,
  getExhibitionSettings,
  getSelectedSendingOption,
  (isSelfRegistration, exhibitionSettings, selectedTicketSendingOption) => {
    if (isSelfRegistration) {
      return false;
    }

    if (exhibitionSettings?.isTicketHolderVisible) {
      return true;
    }

    const isSelectedTicketSendingOptionToBuyer = includes(
      [
        TicketSendingOption.AllToBuyer,
        TicketSendingOption.MobilePerBuyer,
        TicketSendingOption.TicketRetrievalLinkBuyer
      ],
      selectedTicketSendingOption
    );

    return !isSelectedTicketSendingOptionToBuyer;
  }
);

export const showLoginOnTicketPage = createSelector(getLoginMode, isUserLoggedIn, (loginMode, isLoggedIn) => {
  const result = loginMode === LoginOptions.OptionalTicketLogin && !isLoggedIn;
  return result;
});

export const isLastNameForForgottenPasswordRequired = createSelector(
  getExhibitionSettings,
  getOperatorsSettings,
  (exhibition, operator) => {
    if (exhibition && exhibition.enableLastNameForForgottenPassword) {
      return true;
    }
    if (operator && operator.enableLastNameForForgottenPassword) {
      return true;
    }
    return false;
  }
);

export const notLoggedAndLoginMandatory = createSelector(getLoginMode, isUserLoggedIn, (loginMode, isLoggedIn) => {
  const result = loginMode === LoginOptions.BeforeTicketSelection && !isLoggedIn;
  return result;
});

export const selectIsNotLoggedInAndOptionalTicketLogin = createSelector(
  getLoginMode,
  isUserLoggedIn,
  (loginMode, isLoggedIn) =>
    includes(
      [
        LoginOptions.OptionalTicketLogin,
        LoginOptions.OptionalTicketAndPersonalLogin
      ],
      loginMode) && !isLoggedIn
);

export const showLoginOnPersonalPage = createSelector(getLoginMode, isUserLoggedIn, (loginMode, isLoggedIn) => {
  const result = loginMode === LoginOptions.OptionalPersonalLogin && !isLoggedIn;
  return result;
});

export const showLoginOnTicketAndPersonalPage = createSelector(
  getLoginMode,
  isUserLoggedIn,
  (loginMode, isLoggedIn) => {
    const result = loginMode === LoginOptions.OptionalTicketAndPersonalLogin && !isLoggedIn;
    return result;
  }
);

export const isBuyerOnPersonalPageHidden = createSelector(getLoginMode, isUserLoggedIn, (loginMode, isLoggedIn) => {
  const result =
    (loginMode === LoginOptions.OptionalPersonalLogin ||
      loginMode === LoginOptions.OptionalTicketAndPersonalLogin ||
      loginMode === LoginOptions.PersonalizationWithRequiredEmailConfirmation) &&
    !isLoggedIn;
  return result;
});

export const hideTopBarLogin = createSelector(getLoginMode, isUserLoggedIn, (loginMode, isLoggedIn) => {
  const result = loginMode === LoginOptions.HideTopBarLogin && !isLoggedIn;
  return result;
});

export const isUserLoggedInAndVerified = createSelector(isUserLoggedIn, getProfile, (isLoggedIn, userProfile) => {
  return isLoggedIn && userProfile && userProfile.isEmailVerified;
});

/**
 * Returns true if loginMode is 'personalizationWithRequiredEmailConfirmation' and
 * the user isn't logged in or his/her e-mail isn't verified yet.
 */
export const isConfirmedEmailRequiredOnPersonalization = createSelector(
  getLoginMode,
  isUserLoggedInAndVerified,
  (loginMode, isUserLoggedInAndVerified) => {
    return loginMode === LoginOptions.PersonalizationWithRequiredEmailConfirmation && !isUserLoggedInAndVerified;
  }
);

export const selectIsAddTicketsAfterLogin = createSelector(
  selectExhibitionSettingsLoginMode,
  (exhibitionSettingsLoginMode) => exhibitionSettingsLoginMode === LoginOptions.AddTicketsAfterLogin
);

export const selectCheckTariffLimitPerUserAccount = createSelector(
  isUserLoggedIn,
  selectExhibitionSettingsLoginMode,
  (isUserLoggedIn, exhibitionSettingsLoginMode) =>
    !isUserLoggedIn && includes(CheckUserAccountTicketLimitLoginOptions, exhibitionSettingsLoginMode)
);

const selectCheckUserAccountTariffLimit = createSelector(
  getSelectedExhibitionId,
  selectExhibitionSettingsLoginMode,
  isUserLoggedIn,
  isUserLoggedInAndVerified,
  (exhibitionId, exhibitionSettingsLoginMode, isUserLoggedIn, isUserLoggedInAndVerified) => {
    if (isNull(exhibitionId)) {
      return false;
    }

    if (
      isUserLoggedInAndVerified &&
      LoginOptions.BeforeTicketSelectionWithRequiredEmailConfirmation === exhibitionSettingsLoginMode
    ) {
      return true;
    }

    return isUserLoggedIn && includes(CheckUserAccountTicketLimitLoginOptions, exhibitionSettingsLoginMode);
  }
);

export const selectUserAccountTariffLimitCheck = createSelector(
  selectCheckUserAccountTariffLimit,
  getSelectedExhibitionId,
  getTicketLimitPerUserAccount,
  (checkUserAccountTariffLimit, exhibitionId, ticketLimitPerUserAccount) => ({
    checkUserAccountTariffLimit: checkUserAccountTariffLimit && !isNull(ticketLimitPerUserAccount),
    exhibitionId
  })
);

export const selectUserAccountTariffSpecificLimitCheck = createSelector(
  selectCheckUserAccountTariffLimit,
  getSelectedExhibitionId,
  (checkUserAccountTariffSpecificLimit, exhibitionId) => ({
    checkUserAccountTariffSpecificLimit,
    exhibitionId
  })
);

export const selectIsPhoneVerificationRequired = createSelector(
  getOperatorsSettings,
  getProfile,
  (operatorsSettings, profile) => operatorsSettings?.isSIAEEnabled && (isNull(profile) || !profile.isPhoneVerified)
);

export const selectIsMaintenanceEnabled = (eventId: number | null) =>
  createSelector(
    selectIsOperatorsMaintenanceEnabled,
    selectIsEventMaintenanceEnabled,
    (isEventMaintenanceEnabled, isOperatorsMaintenanceEnabled) => {
      if (isOperatorsMaintenanceEnabled) {
        return true;
      }

      if (isNull(eventId)) {
        return isOperatorsMaintenanceEnabled;
      }

      return isEventMaintenanceEnabled;
    }
  );
